import { useEffect, useMemo, useRef, useState } from "react";

import { useIntl } from "react-intl";
import moment from "moment";
import { ImageOff, Menu, MessageCircle, Phone } from "lucide-react";

import ChatBox from "react-lib/apps/MSG/ChatBox";
import { handleNavigationMessage } from "react-lib/apps/IsHealth/Chat/MessageContentHTML";
import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";

import { AlertV4, ButtonV4 } from "../common/ui";
import { errorTextOf } from "../common/errorTextV4";

import type { CardChatV4Props } from "../chat/CardChatV4";
import {
  ACTIONS as EXAM_ACTIONS,
  SEQUENCE_NAME as EXAM_SEQUENCE,
  type ExamRunSequenceProp,
} from "../common/sequence/ExamV4";
import {
  ACTIONS,
  BTN_ACTS,
  SEQUENCE_NAME,
  type PickedState,
  type RunSequence,
  type SetProp,
  type State,
} from "../common/sequence/TeleHealthV4";
import { CARD_NAMES } from "./examDispatchV4";
import { type ExamIds } from "./examModelV4";
import { dataUriFromImageUrl } from "./galleryRasterV4";
import { GALLERY_TYPES } from "./mockPatientV4";
import {
  activeCallOf,
  chatFilesOf,
  quickRepliesOf,
  todayTelemed,
  type TeleFile,
} from "./teleHealthModelV4";

import "./_hisv4-tele.scss";

// Types
export type TeleChatDetail = {
  chat_channel?: number | null;
  unread_message_count?: number;
};

type CardTeleHealthV4Props = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  TeleHealthV4Sequence?: State["TeleHealthV4Sequence"];
  runSequence: RunSequence & ExamRunSequenceProp;
  setProp?: SetProp;
  patient: { id?: number; hn: string; name: string };
  encounter?: Record<string, any> | null;
  examIds: ExamIds;
  divisionId?: number;
  chatDetail?: TeleChatDetail | null;
  // controller
  chatController: any;
  classifyController?: any;
  diagFormController?: any;
  // data
  apiToken: string;
  userId: string;
  // callback
  onOpenVideoCall?: CardChatV4Props["onOpenVideoCall"];
  onSelectMenu: (menu: "doctor") => void;
};

// Const
const UPLOAD_KEY = `${CARD_NAMES.gallery}_${EXAM_ACTIONS.UPLOAD_PHOTO}`;

const GALLERY_TYPE = GALLERY_TYPES[0].value;

const CHAT_BOX_STYLE = { flex: "1 1 0", height: "auto", minHeight: 0 };

const CardTeleHealthV4 = (props: CardTeleHealthV4Props) => {
  const intl = useIntl();

  const [filesOpen, setFilesOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [draftText, setDraftText] = useState<{ text: string } | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedIds, setSavedIds] = useState<number[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [brokenIds, setBrokenIds] = useState<number[]>([]);
  const [callRound, setCallRound] = useState(0);

  const threadRef = useRef<HTMLDivElement | null>(null);

  const seq = props.TeleHealthV4Sequence;

  const chatChannelId = props.chatDetail?.chat_channel;

  const uploadStatus = props.buttonLoadCheck?.[UPLOAD_KEY];

  // ข้อความยังไม่มา (LOAD ล้าง messages)
  const messagesLoading =
    props.buttonLoadCheck?.[BTN_ACTS.LOAD_MESSAGES] === BLC_STATUS.LOADING ||
    (seq?.messages === undefined && !props.errorMessage?.[BTN_ACTS.LOAD_MESSAGES]);

  // Memo
  const appointment = useMemo(
    () => todayTelemed(seq?.appointments ?? [], moment()),
    [seq?.appointments],
  );

  const quickReplies = useMemo(
    () => quickRepliesOf(seq?.templates ?? [], props.divisionId),
    [seq?.templates, props.divisionId],
  );

  const files = useMemo(() => chatFilesOf(seq?.messages ?? []), [seq?.messages]);

  const callUrl = useMemo(() => activeCallOf(seq?.messages ?? []), [seq?.messages]);

  // Effect
  useEffect(() => {
    const thread = threadRef.current;

    if (!thread) {
      return;
    }

    // รูปในข้อความที่โหลดไม่ได้
    const handleImageError = (e: Event) => {
      const target = e.target as HTMLElement;

      if (target.tagName === "IMG" && target.parentElement) {
        target.dataset.hv4Broken = "true";
        target.parentElement.dataset.hv4Broken = "true";
      }
    };

    thread.addEventListener("error", handleImageError, true);

    return () => thread.removeEventListener("error", handleImageError, true);
  }, [chatChannelId]);

  useEffect(() => {
    if (!props.patient.id) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      patientId: props.patient.id,
      divisionId: props.divisionId,
    });
  }, [props.patient.id, props.divisionId]);

  useEffect(() => {
    if (savingId === null || uploadStatus === BLC_STATUS.LOADING) {
      return;
    }

    if (uploadStatus === BLC_STATUS.SUCCESS) {
      setSavedIds((ids) => [...ids, savingId]);
    } else {
      setFileError(errorTextOf(props.errorMessage?.[CARD_NAMES.gallery]));
    }

    setSavingId(null);
  }, [uploadStatus]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // วางสายแล้ว โหลดห้องแชทใหม่หลัง end_call ถูกส่ง
    const handleCallMessage = (e: MessageEvent) => {
      try {
        if (JSON.parse(e.data)?.message === "end_call") {
          timer = setTimeout(() => setCallRound((round) => round + 1), 2000);
        }
      } catch {
        // ข้อความอื่นที่ไม่ใช่ JSON
      }
    };

    window.addEventListener("message", handleCallMessage);

    return () => {
      window.removeEventListener("message", handleCallMessage);
      clearTimeout(timer);
    };
  }, []);

  // Handler
  const handleOpenVideoCall = (url: string) => {
    props.onOpenVideoCall?.(url, String(chatChannelId));
  };

  const handleLoadMessages = () => {
    if (chatChannelId) {
      props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.LOAD_MESSAGES, chatChannelId });
    }
  };

  const handleOpenFiles = () => {
    setFilesOpen(!filesOpen);
    setQuickOpen(false);
  };

  const handleOpenQuickReply = () => {
    setQuickOpen(!quickOpen);
    setFilesOpen(false);
  };

  const handleSaveFile = async (file: TeleFile) => {
    setFileError(null);

    const result = await dataUriFromImageUrl(file.url);

    if (result.ok === false) {
      setFileError(result.error);

      return;
    }

    setSavingId(file.id);

    props.runSequence({
      sequence: EXAM_SEQUENCE,
      action: EXAM_ACTIONS.UPLOAD_PHOTO,
      card: CARD_NAMES.gallery,
      ids: props.examIds,
      image: result.image,
      description: `${GALLERY_TYPE}: ${file.name}`,
    });
  };

  const handlePickQuickReply = (text: string) => {
    setDraftText({ text });
    setQuickOpen(false);
  };

  const handleCloseError = () => {
    props.setProp?.(`errorMessage.${BTN_ACTS.LOAD}`, null);
    props.setProp?.(`errorMessage.${BTN_ACTS.LOAD_MESSAGES}`, null);
  };

  const handleCloseFileError = () => {
    setFileError(null);
  };

  const handleImageError = (id: number) => {
    setBrokenIds((ids) => [...ids, id]);
  };

  const handleReadMessage = () => {
    props.setProp?.("chatDetail.unread_message_count", 0);
  };

  // Render
  return (
    <div className="hv4-tele">
      <div className="hv4-tele-title">
        <h1>TeleHealth</h1>
        <span>
          ห้องแชทผู้รับบริการ
          {appointment
            ? ` · Telemedicine นัดวันนี้ ${appointment.time} กับ ${appointment.doctor}`
            : ""}
        </span>
      </div>

      <AlertV4
        tone="danger"
        error={props.errorMessage?.[BTN_ACTS.LOAD] || props.errorMessage?.[BTN_ACTS.LOAD_MESSAGES]}
        onClose={handleCloseError}
      />

      <section className="hv4-tele-box" aria-label="ห้องแชทผู้รับบริการ">
        {/* หัวการ์ด */}
        <div className="hv4-tele-head">
          <b>
            Telemed
            <Phone aria-hidden="true" /> · {props.patient.name} (HN {props.patient.hn})
          </b>
          {chatChannelId ? (
            <span className="hv4-tele-head-actions">
              {callUrl ? (
                <button
                  type="button"
                  className="hv4-tele-join-btn"
                  onClick={() => handleOpenVideoCall(callUrl)}
                >
                  <Phone aria-hidden="true" />
                  เข้าร่วม Telemed
                </button>
              ) : null}
              <button
                type="button"
                className="hv4-tele-files-btn"
                aria-expanded={filesOpen}
                onClick={handleOpenFiles}
              >
                ไฟล์ในห้องแชท ({files.length})
              </button>
            </span>
          ) : null}
        </div>

        {/* ไฟล์ในห้องแชท */}
        {filesOpen ? (
          <div className="hv4-tele-files">
            <div className="hv4-tele-files-note">
              ภาพที่ผู้รับบริการส่งมา บันทึกเข้า Image Gallery ของ Encounter ได้
            </div>
            {fileError ? (
              <AlertV4 tone="danger" onClose={handleCloseFileError}>
                {fileError}
              </AlertV4>
            ) : null}
            {files.map((file) => {
              const isSaved = savedIds.includes(file.id);

              const isBroken = brokenIds.includes(file.id);

              return (
                <div key={file.id} className="hv4-tele-file">
                  {file.isImage ? (
                    <span className="hv4-tele-file-thumb">
                      {isBroken ? (
                        <ImageOff aria-hidden="true" />
                      ) : (
                        <img
                          src={file.url}
                          alt={file.name}
                          onError={() => handleImageError(file.id)}
                        />
                      )}
                    </span>
                  ) : null}
                  <span className="hv4-tele-file-name">
                    <b>{file.name}</b>{" "}
                    <small>
                      · {file.author} · {file.date} {file.time}
                    </small>
                  </span>
                  {file.isImage ? (
                    <button
                      type="button"
                      className={isSaved ? "is-saved" : ""}
                      disabled={isSaved || isBroken || savingId !== null}
                      onClick={() => handleSaveFile(file)}
                    >
                      {isSaved ? "บันทึกแล้ว" : "บันทึกเข้า Image Gallery"}
                    </button>
                  ) : null}
                </div>
              );
            })}
            {files.length === 0 ? (
              <div className="hv4-tele-files-none">
                {messagesLoading ? "กำลังโหลด" : "ยังไม่มีไฟล์"}
              </div>
            ) : null}
          </div>
        ) : null}

        {chatChannelId ? (
          <div ref={threadRef} className="hv4-chat-thread hv4-tele-thread">
            <ChatBox
              key={`${chatChannelId}-${callRound}`}
              // controller
              controller={props.chatController}
              classifyController={props.classifyController}
              diagFormController={props.diagFormController}
              // data
              userId={props.userId}
              apiToken={props.apiToken}
              division_id={props.divisionId ?? 0}
              isPatient={false}
              nullId={false}
              readOnly={false}
              fullname="fullname"
              username="username"
              encounterId={props.encounter?.id}
              selectedEncounter={props.encounter}
              match={{ params: { chatChannelId } }}
              patientId={props.patient.id}
              patientData={{}}
              draftText={draftText}
              // config
              hideButtonMenu={true}
              chatBoxStyle={CHAT_BOX_STYLE}
              placeholder="พิมพ์ข้อความถึงผู้รับบริการ"
              quickReply={
                quickOpen ? (
                  <div className="hv4-tele-quick">
                    {quickReplies.map((text, index) => (
                      <button
                        key={`${index}-${text}`}
                        type="button"
                        onClick={() => handlePickQuickReply(text)}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                ) : null
              }
              textBoxPrefix={
                <button
                  type="button"
                  className="hv4-tele-quick-btn"
                  aria-label="ข้อความสำเร็จรูป"
                  title="ข้อความสำเร็จรูป"
                  aria-expanded={quickOpen}
                  onClick={handleOpenQuickReply}
                >
                  <Menu aria-hidden="true" />
                </button>
              }
              emptyMessage={
                <div className="hv4-tele-empty">
                  <MessageCircle aria-hidden="true" />
                  <b>ยังไม่มีข้อความ</b>
                  <span>พิมพ์ข้อความด้านล่างเพื่อเริ่มคุยกับผู้รับบริการ</span>
                </div>
              }
              choiceRight={
                <ButtonV4 className="hv4-tele-link" onClick={() => props.onSelectMenu("doctor")}>
                  ไปหน้าตรวจรักษา
                </ButtonV4>
              }
              // callback
              onDidMount={handleReadMessage}
              onNavigationMessage={(content: any) => handleNavigationMessage(content, intl)}
              onCallGetLastMessage={handleLoadMessages}
              onOpenVideoCall={handleOpenVideoCall}
              onCloseVideoCall={() => {}}
              onSetChannelName={() => {}}
            />
          </div>
        ) : (
          <div className="hv4-tele-none">ยังไม่มีห้องแชทของผู้รับบริการนี้</div>
        )}
      </section>
    </div>
  );
};

CardTeleHealthV4.displayName = "CardTeleHealthV4";

export default CardTeleHealthV4;
